import React, { useEffect, useState } from 'react';
import Button from '../common/Button.js';
import FormInput from '../common/FormInput.js';
import SearchableSelectField from '../common/SearchableSelectField.js';
import useAppStore from '../../store/useAppStore.js';

const BranchSetup = () => {
  const currentBranch = useAppStore((state) => state.selectedBranch);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isResettingToken, setIsResettingToken] = useState(false);

  const updateBranchReceiptData = useAppStore((state) => state.updateBranchReceiptData);
  const resetBranchTokenNumber = useAppStore((state) => state.resetBranchTokenNumber);
  const fetchBranchReceiptData = useAppStore((state) => state.fetchBranchReceiptData);
  const initializeOfflineMode = useAppStore((state) => state.initializeOfflineMode);
  const disableOfflineMode = useAppStore((state) => state.disableOfflineMode);
  const offlineMode = useAppStore((state) => state.offlineMode);
  const offlineSyncing = useAppStore((state) => state.offlineSyncing);
  const branchReceiptData = useAppStore((state) => state.branchReceiptData) || {};


  const [branchFormData, setBranchFormData] = useState({
    name: '',
    location: '',
    state: 'Active',
    manager: '',
    managerPhone: '',
    timezone: 'UTC'
  });

  const [receiptFormData, setReceiptFormData] = useState(branchReceiptData);

  const stateOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Maintenance', label: 'Maintenance' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'CAD', label: 'CAD (C$)' },
    { value: 'PKR', label: 'PKR (Rs)' }
  ];

  const discountTypeOptions = [
    { value: 'fixed', label: 'Fixed Amount' },
    { value: 'percentage', label: 'Percentage' }
  ];

  useEffect(() => {
    if (!currentBranch) return;

    const managerValue = currentBranch.manager === 'Not assigned' ? '' : currentBranch.manager;
    const managerPhoneValue = currentBranch.managerPhone === 'Not provided' ? '' : currentBranch.managerPhone;

    setBranchFormData({
      name: currentBranch.name,
      location: currentBranch.location,
      state: currentBranch.state,
      manager: managerValue,
      managerPhone: managerPhoneValue,
      timezone: currentBranch.timezone || 'UTC'
    });
  }, [currentBranch]);

  useEffect(() => {
    if (branchReceiptData && Object.keys(branchReceiptData).length > 0) {
      setReceiptFormData(branchReceiptData);
    }
  }, [branchReceiptData]);

  // Form Handlers
  const handleBranchInputChange = (e) => {
    const { name, value } = e.target;
    setBranchFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReceiptInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setReceiptFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!currentBranch) {
      setError('No branch selected');
      return;
    }

    if (!branchFormData.name || !branchFormData.location) {
      setError('Please fill in the branch name and location');
      return;
    }

    try {
      const branchId = parseInt(String(currentBranch.id).replace('BR-', ''), 10);
      const payload = {
        ...receiptFormData,
        timezone: branchFormData.timezone || 'UTC',
        name: branchFormData.name,
        location: branchFormData.location,
        state: branchFormData.state,
        manager: branchFormData.manager,
        managerPhone: branchFormData.managerPhone,
        branch_id: branchId,
        offlineMode: offlineMode
      };

      await updateBranchReceiptData(payload);
      setMessage('Branch settings updated successfully');
      setIsEditing(false);
      setTimeout(() => setMessage(''), 6000);
    } catch (err) {
      setError(err.message || 'Failed to save branch settings');
    }
  };

  const handleToggleOfflineMode = async () => {
    if (!currentBranch) {
      setError('No branch selected');
      return;
    }

    setError('');
    setMessage('');

    const branchId = String(currentBranch.id).replace(/^BR-/, '');
    try {
      if (!offlineMode) {
        await initializeOfflineMode(branchId);
        setMessage('Branch switched to offline mode. Sales and orders will use local data.');
      } else {
        await disableOfflineMode(branchId);
        setMessage('Offline data synchronized and branch switched back to online mode.');
      }
    } catch (err) {
      setError(err.message || 'Failed to change offline mode.');
    }
  };

  if (!currentBranch) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading branch settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Branch Settings</h1>
          <p className="text-gray-700">Manage current branch and receipt settings</p>
        </div>
        <div className="flex items-center gap-2">
          {/* <Button
            onClick={handleToggleOfflineMode}
            className={`${offlineMode ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-800'} text-white`}
            disabled={offlineSyncing}
          >
            {offlineSyncing ? (offlineMode ? 'Syncing…' : 'Preparing…') : offlineMode ? 'Switch to Online' : 'Switch to Offline'}
          </Button> */}
          <Button
            onClick={async () => {
              const confirmed = window.confirm('Reset the branch token counter and start from 1? This cannot be undone.');
              if (!confirmed) {
                return;
              }

              setError('');
              setMessage('');
              setIsResettingToken(true);
              try {
                await resetBranchTokenNumber(String(currentBranch.id).replace(/^BR-/, ''));
                // Refresh the data to ensure UI is in sync
                await fetchBranchReceiptData(String(currentBranch.id).replace(/^BR-/, ''));
                setMessage('Token number reset to 1 for this branch');
              } catch (err) {
                setError(err.message || 'Failed to reset token number');
              } finally {
                setIsResettingToken(false);
              }
            }}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            disabled={isResettingToken}
          >
            {isResettingToken ? 'Resetting…' : 'Reset Token Number'}
          </Button>
          <Button
            onClick={() => setIsEditing(!isEditing)}
            className={`${
              isEditing
                ? 'bg-gray-400 hover:bg-gray-500'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            {isEditing ? '✕ Cancel' : '✎ Edit Settings'}
          </Button>
        </div>
      </div>

      {offlineMode && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
          <p className="font-semibold">Offline mode is active.</p>
          <p className="text-sm">Sales, orders and branch setup will use local offline data until you switch back to online.</p>
        </div>
      )}

      {/* Messages */}
      {message && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Receipt Settings Section */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6">Receipt Settings</h2>

        <div className="space-y-6">
          {/* Company Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
              Store Contact Information
            </h3>

            {isEditing ? (
              <>
                <FormInput
                  label="Shop Name"
                  name="shopName"
                  value={receiptFormData.shopName}
                  onChange={handleReceiptInputChange}
                  placeholder="e.g., Ice Cream Shop"
                  required
                />

                <FormInput
                  label="Address"
                  name="address"
                  value={receiptFormData.address}
                  onChange={handleReceiptInputChange}
                  placeholder="123 Main Street, Downtown"
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={receiptFormData.phone}
                    onChange={handleReceiptInputChange}
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                  <FormInput
                    label="Email"
                    name="email"
                    type="email"
                    value={receiptFormData.email}
                    onChange={handleReceiptInputChange}
                    placeholder="info@icecreamshop.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Website"
                    name="website"
                    value={receiptFormData.website}
                    onChange={handleReceiptInputChange}
                    placeholder="www.icecreamshop.com"
                  />
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Shop Name</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.shopName || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Address</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.address || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Phone</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.phone || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Email</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.email || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Website</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.website || '-'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Receipt Display Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
              Receipt Display Settings
            </h3>

            {isEditing ? (
              <>
                <SearchableSelectField
                  label="Currency"
                  name="currency"
                  value={receiptFormData.currency}
                  onChange={handleReceiptInputChange}
                  options={currencyOptions}
                />

                <FormInput
                  label="Footer Message"
                  name="footerMessage"
                  value={receiptFormData.footerMessage}
                  onChange={handleReceiptInputChange}
                  placeholder="Thank you for your purchase!"
                />

                <FormInput
                  label="Token Papers"
                  name="tokenPapers"
                  type="number"
                  min="1"
                  value={receiptFormData.tokenPapers}
                  onChange={handleReceiptInputChange}
                  placeholder="1"
                  required
                />

                <FormInput
                  label="Shift Start Hour (0-23)"
                  name="shiftStartHour"
                  type="number"
                  value={receiptFormData.shiftStartHour}
                  onChange={handleReceiptInputChange}
                  placeholder="15"
                  min="0"
                  max="23"
                />

                <FormInput
                  label="Return Policy"
                  name="returnPolicy"
                  value={receiptFormData.returnPolicy}
                  onChange={handleReceiptInputChange}
                  placeholder="30 days money back guarantee"
                />
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="sendTokenToStaff"
                    name="sendTokenToStaff"
                    checked={receiptFormData.sendTokenToStaff}
                    onChange={handleReceiptInputChange}
                    className="h-4 w-4 rounded"
                  />
                  <label htmlFor="sendTokenToStaff" className="ml-2 text-sm font-medium text-gray-700">
                    Send token to branch staff (when enabled)
                  </label>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Currency</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {currencyOptions.find((c) => c.value === receiptFormData.currency)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Tax ID</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.taxId || '-'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Footer Message</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.footerMessage || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Shift Start Hour</p>
                  <p className="text-lg font-semibold text-gray-800">{typeof receiptFormData.shiftStartHour !== 'undefined' ? receiptFormData.shiftStartHour : 15}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Send Token To Staff</p>
                  <p className="text-lg font-semibold text-gray-800">{receiptFormData.sendTokenToStaff ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Token Papers</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.tokenPapers || 1}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Return Policy</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.returnPolicy || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Next Token Number</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receiptFormData.tokenNumber != null ? receiptFormData.tokenNumber : 'Auto'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Discount Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
              Discount Settings
            </h3>

            {isEditing ? (
              <>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableAutoDiscount"
                    name="enableAutoDiscount"
                    checked={receiptFormData.enableAutoDiscount}
                    onChange={handleReceiptInputChange}
                    className="h-4 w-4 rounded"
                  />
                  <label htmlFor="enableAutoDiscount" className="ml-2 text-sm font-medium text-gray-700">
                    Enable Auto Discount for All Orders
                  </label>
                </div>

                {receiptFormData.enableAutoDiscount && (
                  <>
                    <SearchableSelectField
                      label="Discount Type"
                      name="discountType"
                      value={receiptFormData.discountType}
                      onChange={handleReceiptInputChange}
                      options={discountTypeOptions}
                    />

                    <FormInput
                      label={receiptFormData.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                      name="discountValue"
                      type="number"
                      value={receiptFormData.discountValue}
                      onChange={handleReceiptInputChange}
                      placeholder={receiptFormData.discountType === 'percentage' ? '10' : '5.00'}
                      step={receiptFormData.discountType === 'percentage' ? '0.01' : '0.01'}
                      min="0"
                    />
                  </>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Auto Discount Enabled:{' '}
                  <span className="font-semibold text-gray-800">
                    {receiptFormData.enableAutoDiscount ? 'Yes' : 'No'}
                  </span>
                </p>
                {receiptFormData.enableAutoDiscount && (
                  <>
                    <p className="text-sm text-gray-600">
                      Discount Type:{' '}
                      <span className="font-semibold text-gray-800">
                        {discountTypeOptions.find((opt) => opt.value === receiptFormData.discountType)?.label || receiptFormData.discountType}
                      </span>
                    </p>
                    <p className="text-sm text-gray-600">
                      Discount Value:{' '}
                      <span className="font-semibold text-gray-800">
                        {receiptFormData.discountType === 'percentage' ? `${receiptFormData.discountValue}%` : `${receiptFormData.discountValue}`}
                      </span>
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Preview Section */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-4">Receipt Preview</h2>
        <div className="bg-gray-50 p-6 rounded border border-gray-300 text-xs text-center space-y-1 font-mono max-w-sm mx-auto">
          <div className="font-bold text-sm">{branchFormData.name}</div>
          <div>{receiptFormData.address}</div>
          <div>{receiptFormData.phone}</div>
          {receiptFormData.email && <div>{receiptFormData.email}</div>}
          {receiptFormData.website && <div>{receiptFormData.website}</div>}
          <div className="border-t pt-2 mt-2">
            <div>Item 1 ................ 5.00</div>
            <div>Subtotal ............. 5.00</div>
            {receiptFormData.enableAutoDiscount && receiptFormData.discountValue > 0 && (
              <div>Discount ({receiptFormData.discountType === 'percentage' ? `${receiptFormData.discountValue}%` : `$${receiptFormData.discountValue}`}) ........... ${
                receiptFormData.discountType === 'percentage'
                  ? `${(5 * receiptFormData.discountValue / 100).toFixed(2)}`
                  : `${Math.min(receiptFormData.discountValue, 5).toFixed(2)}`
              }</div>
            )}

            <div className="font-bold border-t pt-1 mt-1">
              Total ............... {
                (() => {
                  const subtotal = 5;
                  const discount = receiptFormData.enableAutoDiscount && receiptFormData.discountValue > 0
                    ? (receiptFormData.discountType === 'percentage'
                        ? subtotal * receiptFormData.discountValue / 100
                        : Math.min(receiptFormData.discountValue, subtotal))
                    : 0;
                  const afterDiscount = subtotal - discount;
                  const tax = receiptFormData.showTaxOnReceipt
                    ? afterDiscount * receiptFormData.taxPercentage / 100
                    : 0;
                  return `${(afterDiscount + tax).toFixed(2)}`;
                })()
              }
            </div>
          </div>
          <div className="border-t pt-2 mt-2 text-xs">
            {receiptFormData.footerMessage}
          </div>
          {receiptFormData.returnPolicy && (
            <div className="text-xs italic">{receiptFormData.returnPolicy}</div>
          )}
        </div>
      </div>

      {/* Save Button */}
      {isEditing && (
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => setIsEditing(false)}
            className="bg-gray-400 hover:bg-gray-500 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveChanges}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default BranchSetup;